/* eslint-disable no-unused-vars */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery,
  Grid
} from '@mui/material';

// project import
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { ThemeMode } from 'config';

// assets
import { BellOutlined, CheckCircleOutlined, ClearOutlined, MessageOutlined } from '@ant-design/icons';
import useNotifications from 'hooks/Notifications/useNotification';
import { getTimeAgoOrDate, getTimeFromDateString } from 'utils/common';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const { unreadNotifications, markAsReadNotificationsAction } = useNotifications();
  const anchorRef = useRef(null);
  const [read, setRead] = useState(0);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    // When unreadNotifications change, update the 'read' count
    if (unreadNotifications) {
      setRead(unreadNotifications?.length);
    }
  }, [unreadNotifications]);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleNotification = (target, id) => {
    // console.log(target, 'id', id);
    navigate(`/feedback/${target}`);
    markAsReadNotificationsAction({ params: { notification_ids: [id] } });
  };

  const handleClearNotifications = () => {
    const ids = unreadNotifications.map((_el) => {
      return _el.id;
    });
    markAsReadNotificationsAction({ params: { notification_ids: ids } });
    // alert('hi'); // Implement this function in your custom hook to clear notifications
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.50' : 'grey.100';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.50';

  return (
    <Box sx={{ flexShrink: 0, ml: 2 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={read} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} sx={{ overflow: 'hidden' }} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  // secondary={
                  //   <>
                  //     {read > 0 && (
                  //       <Tooltip title="Mark as all read">
                  //         <IconButton color="success" size="small" onClick={() => setRead(0)}>
                  //           <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                  //         </IconButton>
                  //       </Tooltip>
                  //     )}
                  //   </>
                  // }
                  secondary={
                    read > 0 && ( // Display the "Clear" button only when there are unread notifications
                      <Tooltip title="Clear All">
                        <IconButton color="success" size="small" onClick={handleClearNotifications}>
                          <ClearOutlined style={{ fontSize: '1.15rem' }} />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {unreadNotifications && unreadNotifications?.length > 0 ? (
                      <>
                        <Grid sx={{ maxHeight: 'calc(100vh - 502px)', overflow: 'auto' }}>
                          {unreadNotifications &&
                            unreadNotifications.map((notify, index) => {
                              return (
                                <Fragment key={index}>
                                  <ListItemButton selected={read > 0} onClick={() => handleNotification(notify.target, notify.id)}>
                                    <ListItemAvatar>
                                      <Avatar
                                        sx={{
                                          color: 'success.main',
                                          bgcolor: 'success.lighter'
                                        }}
                                      >
                                        <MessageOutlined />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={<Typography variant="h6">{notify.description}</Typography>}
                                      secondary={notify.timestamp.split(' ')[0]}
                                    />
                                    <ListItemSecondaryAction>
                                      <Typography variant="caption" noWrap>
                                        {/* {getTimeFromDateString(notify.timestamp)} */}
                                        {notify.timestamp.split(' ')[1]}
                                      </Typography>
                                    </ListItemSecondaryAction>
                                  </ListItemButton>
                                  <Divider />
                                </Fragment>
                              );
                            })}
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle1">No notifications to display</Typography>
                        </Box>
                      </>
                    )}

                    {/* <ListItemButton selected={read > 0}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: 'primary.main',
                            bgcolor: 'primary.lighter'
                          }}
                        >
                          <MessageOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                            <Typography component="span" variant="subtitle1">
                              Aida Burg
                            </Typography>{' '}
                            commented your post.
                          </Typography>
                        }
                        secondary="5 August"
                      />
                      <ListItemSecondaryAction>
                        <Typography variant="caption" noWrap>
                          6:00 PM
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider />
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: 'error.main',
                            bgcolor: 'error.lighter'
                          }}
                        >
                          <SettingOutlined />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                            Your Profile is Complete &nbsp;
                            <Typography component="span" variant="subtitle1">
                              60%
                            </Typography>{' '}
                          </Typography>
                        }
                        secondary="7 hours ago"
                      />
                      <ListItemSecondaryAction>
                        <Typography variant="caption" noWrap>
                          2:45 PM
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider />
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            color: 'primary.main',
                            bgcolor: 'primary.lighter'
                          }}
                        >
                          C
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                            <Typography component="span" variant="subtitle1">
                              Cristina Danny
                            </Typography>{' '}
                            invited to join{' '}
                            <Typography component="span" variant="subtitle1">
                              Meeting.
                            </Typography>
                          </Typography>
                        }
                        secondary="Daily scrum meeting time"
                      />
                      <ListItemSecondaryAction>
                        <Typography variant="caption" noWrap>
                          9:10 PM
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    <Divider /> */}
                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }} href="/notifications">
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Notification;
